/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell. */ import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { ConfigurationVariables } from "../../config";
import { UserDto } from "../../models";
import UserState from "../../models/storeModels/UserState";
import { AppDispatch, RootState } from "..";
import { userActions } from "./user-slice";
import { useSelector } from "react-redux";

/// Fetch users based on tenantId
export const getUserListBasedOnForgeCustomerId = createAsyncThunk(
  "user/getUserListBasedOnForgeCustomerId",
  async (forgeCustomerId: string, { rejectWithValue }: any) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
          customerId: forgeCustomerId,
        },
      };

      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response?.status !== 200) {
        return rejectWithValue(response);
      }
      if (response?.data?.length > 0) {
        const responseWIthId = response?.data?.map((x: any) => ({
          ...x,
          id: x.userId,
        }));
        return responseWIthId;
      } else {
        return response.data;
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

/// Delete user
export const deleteTenantUser = createAsyncThunk(
  "tenant/user/deleteUser",
  async (
    userToDelete: {
      customerId: string;
      userIds: Array<string>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.delete(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/users`,
        {
          data: { userId: userToDelete.userIds },
          headers: {
            customerId: userToDelete.customerId,
            Authorization: "Bearer " + getAccessToken(),
          },
        }
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return userToDelete;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Fetch user export list based on tenantId
export const getUserExportListBasedOnForgeCustomerId = createAsyncThunk(
  "user/getUserExportListBasedOnForgeTenantId",
  async (forgeTenantId: string, { rejectWithValue }: any) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
          customerId: forgeTenantId,
        },
      };

      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      if (response.data.length > 0) {
        const excelConversion = response.data.map(
          (user: {
            userId: any;
            firstName: any;
            lastName: any;
            emailId: any;
            roles: any;
          }) => ({
            "User ID": user.userId,
            "First Name": user.firstName,
            "Last Name": user.lastName,
            "Email Id": user.emailId,
            Roles: user.roles.map((role: any) => role.name).join(", "),
          })
        );
        return excelConversion;
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const getAccessToken = () => {
  const accessToken: string = JSON.parse(
    sessionStorage.getItem("oidc.default") as any
  )["tokens"]["accessToken"];
  if (accessToken === undefined || accessToken === null || accessToken === "") {
    throw new Error("Access token is not in valid format");
  }
  return accessToken;
};

//Set Role list for filter
export const setroleList = (uniqueRoles: Array<any>) => {
  return (dispatch: AppDispatch) => {
    dispatch(userActions.showRoleList(uniqueRoles));
  };
};

export const selectedRoles = (selectedValue: string[]) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      userActions.filteredRoleList({
        selectedValue: selectedValue,
        filterApplied: selectedValue.length === 0 ? false : true,
      })
    );
  };
};

export const setFilteredUsers = (FiltredUsers: Array<UserDto>) => {
  return (dispatch: AppDispatch) => {
    dispatch(userActions.setFilteredUserList(FiltredUsers));
  };
};

/// Fetch User export list
export const exportUserListBasedOnForgeCustomerId = createAsyncThunk(
  "user/exportUserListBasedOnForgeCustomerId",
  async (forgeCustomerId: string, { rejectWithValue }: any) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
          customerId: forgeCustomerId,
        },
      };
      const response: any = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      type Role = {
        roleId: string;
        name: string;
      };

      type UserT = {
        userId: string;
        firstName: string;
        lastName: string;
        emailId: string;
        phone: string;
        roles: Role[];
      };

      type ConvertedData = {
        "User Id": string;
        roles: string;
      };

      const convertedData: ConvertedData[] = response.data.map((data: any) => {
        const Roles: string = data.roles
          .map((role: Role) => role.name)
          .join(",");
        return {
          "User Id": data.userId,
          Roles,
          "First Name": data.firstName,
          "Last Name": data.lastName,
          "Email ID": data.emailId,
        };
      });

      return convertedData;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);
// End Fetch Custoemr Export List

/// Fetch users Permissions for logged in user
export const getUserPermissions = createAsyncThunk(
  "users/userpermissions",
  async (arg, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.UserController}/${ConfigurationVariables.RouteUserPermissions}`,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      if (response.data) {
        return response.data;
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);
